/**
 * /fiscal/configuracao — Configuração fiscal por unidade.
 *
 * Redesign FIN-9: casca .fin2 (breadcrumb + page-title + .cfg-grid + .apikey-row
 * + .block). Mesma lógica de carregar/salvar; adiciona os campos de NFS-e
 * (inscrição municipal, código de serviço e alíquota ISS padrão) que o
 * serializer já expõe e eram obrigatórios para emitir NFS-e.
 *
 * Permissão: fiscal:configurar (Master + gestor fiscal).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiArrowLeft, FiSave } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { PageContainer } from "../../../styles/shared";
import { PESSOA_JURIDICA, mascaraDocumento } from "../../../utils/documento";

const GATEWAYS = [
  { value: "mock", label: "Mock (dev/teste — gera chave fake)" },
  { value: "enotas", label: "eNotas (produção real)" },
];
const REGIMES = [
  { value: "SIMPLES_NACIONAL", label: "Simples Nacional" },
  { value: "LUCRO_PRESUMIDO", label: "Lucro Presumido" },
  { value: "LUCRO_REAL", label: "Lucro Real" },
];
const AMBIENTES = [
  { value: "HOMOLOGACAO", label: "Homologação (teste — não vale fiscalmente)" },
  { value: "PRODUCAO", label: "Produção (NF-e real, válida no SEFAZ)" },
];

export default function ConfiguracaoFiscal() {
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unidadeId, setUnidadeId] = useState("");

  const podeConfigurar = isMaster || hasAllPermissions(["fiscal:configurar"]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = await request("/api/configuracoes-fiscais");
      const list = Array.isArray(p) ? p : p?.results || [];
      if (list.length > 0) {
        setConfig(list[0]);
        setUnidadeId(list[0].business_unit);
      } else {
        setConfig({
          gateway: "mock",
          regime_tributario: "SIMPLES_NACIONAL", ambiente: "HOMOLOGACAO", serie_padrao: 1,
          cfop_padrao: "5102", cfop_interestadual: "6102", natureza_operacao_padrao: "Venda",
          inscricao_municipal: "", codigo_servico_padrao: "", aliquota_iss_padrao: "",
          enotas_empresa_id: "",
          ativo: true, api_key_configurada: false, api_key_plain: "",
        });
        const u = await request("/api/unidades");
        const ulist = Array.isArray(u) ? u : u?.results || [];
        if (ulist.length > 0) setUnidadeId(ulist[0].id);
        // Sem configuração ainda: o emissor já vem da empresa para conferir.
        const empresa = await request("/api/minha-empresa", { silentError: true }).catch(() => null);
        if (empresa?.dados) {
          setConfig((c) => ({
            ...c,
            cnpj_emissor: empresa.dados.cnpj,
            inscricao_estadual: empresa.dados.inscricao_estadual,
            razao_social: empresa.dados.razao_social,
          }));
        }
      }
    } catch (err) {
      setError(err?.message || "Erro ao carregar configuração fiscal.");
    } finally { setLoading(false); }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleSalvar = async () => {
    const body = { ...config, business_unit: unidadeId };
    delete body.id; delete body.created_at; delete body.updated_at;
    delete body.gateway_display; delete body.regime_tributario_display;
    delete body.ambiente_display; delete body.api_key_configurada;
    delete body.ultima_numeracao;
    // O emissor é da empresa (Minha empresa); o backend não aceita aqui.
    delete body.cnpj_emissor; delete body.inscricao_estadual; delete body.razao_social;
    if (!body.api_key_plain) delete body.api_key_plain;
    if (body.aliquota_iss_padrao === "" || body.aliquota_iss_padrao == null) delete body.aliquota_iss_padrao;

    if (config.id) {
      await request(`/api/configuracoes-fiscais/${config.id}`, { method: "PATCH", body: JSON.stringify(body) });
    } else {
      await request("/api/configuracoes-fiscais", { method: "POST", body: JSON.stringify(body) });
    }
    await carregar();
  };

  const set = (campo, valor) => setConfig((c) => ({ ...c, [campo]: valor }));
  const dis = !podeConfigurar;

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Fiscal</span><span className="crumb-sep">/</span>
            <span className="active">Configuração</span>
          </div>
          <h1 className="page-title">Configuração Fiscal</h1>
          <div className="page-sub">Gateway de emissão + dados do emissor + ambiente (NF-e / NFS-e / NFC-e)</div>
        </div>
        <button type="button" className="btn btn--ghost" onClick={() => navigate("/fiscal/notas-fiscais")}>
          <FiArrowLeft size={14} aria-hidden="true" /> Voltar
        </button>
      </div>

      {error && <div className="empty"><div className="msg"><FiAlertTriangle size={14} aria-hidden="true" /> {error}</div></div>}

      {!podeConfigurar && (
        <div className="empty" style={{ marginBottom: 14 }}>
          <div className="msg">Você não tem a permissão <code>fiscal:configurar</code> para editar — campos em modo leitura.</div>
        </div>
      )}

      {loading && !config && <div className="empty"><div className="msg">Carregando…</div></div>}

      {config && (
        <div className="cfg-wrap" style={{ maxWidth: "none" }}>
          <div className="block">
            <div className="block__head"><div className="block__title">Gateway de emissão</div></div>
            <div className="block__body">
              <div className="cfg-grid">
                <div className="input-group">
                  <label className="input-label">Provedor *</label>
                  <select className="select" value={config.gateway} disabled={dis} onChange={(e) => set("gateway", e.target.value)}>
                    {GATEWAYS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
                <div className="input-group">
                  <label className="input-label">Ambiente *</label>
                  <select className="select" value={config.ambiente} disabled={dis} onChange={(e) => set("ambiente", e.target.value)}>
                    {AMBIENTES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                  <span className="input-help">
                    {config.ambiente === "PRODUCAO"
                      ? <span style={{ color: "var(--danger-text)" }}>⚠ NF-e em PRODUÇÃO vale no SEFAZ — gera obrigação fiscal real.</span>
                      : <>Ambiente: <span className={"badge no-dot " + (config.ambiente === "PRODUCAO" ? "success" : "warning")}>{config.ambiente === "PRODUCAO" ? "Produção" : "Homologação"}</span></>}
                  </span>
                </div>
              </div>

              {config.gateway === "enotas" && (
                <div className="input-group" style={{ marginTop: 14 }}>
                  <label className="input-label" htmlFor="enotas-empresa">ID da empresa no eNotas *</label>
                  <input id="enotas-empresa" className="input" disabled={dis} value={config.enotas_empresa_id || ""}
                         onChange={(e) => set("enotas_empresa_id", e.target.value)}
                         onBlur={(e) => set("enotas_empresa_id", e.target.value.trim())}
                         placeholder="Painel do eNotas › Empresas › sua empresa" />
                  <span className="input-help">O eNotas identifica a empresa por este ID, não pelo CNPJ. Sem ele, nenhuma nota sai.</span>
                </div>
              )}

              {config.gateway === "enotas" && (
                <div className="input-group" style={{ marginTop: 14 }}>
                  <label className="input-label">API Key eNotas</label>
                  <div className="apikey-row">
                    <span className={"badge no-dot " + (config.api_key_configurada ? "success" : "neutral")}>
                      {config.api_key_configurada ? "Configurada" : "Não configurada"}
                    </span>
                    <input className="input" type="password" style={{ flex: 1 }} disabled={dis}
                           placeholder={config.api_key_configurada ? "Preenchida (deixe em branco para manter)" : "Cole a API key de app.enotas.com.br"}
                           value={config.api_key_plain || ""} onChange={(e) => set("api_key_plain", e.target.value)} />
                  </div>
                  <span className="input-help">Guardada criptografada. Depois de salva, ela nunca mais aparece na tela.</span>
                </div>
              )}
            </div>
          </div>

          <div className="block">
            <div className="block__head"><div className="block__title">Dados do emissor</div></div>
            <div className="block__body">
              <div className="cfg-grid">
                {/* CNPJ, IE e razão social são da empresa (fonte única): aqui só
                    leitura, com o atalho para onde se altera. */}
                <div className="input-group">
                  <label className="input-label">CNPJ *</label>
                  <input className="input" value={mascaraDocumento(config.cnpj_emissor, PESSOA_JURIDICA)} disabled readOnly />
                </div>
                <div className="input-group">
                  <label className="input-label">Inscrição Estadual *</label>
                  <input className="input" value={config.inscricao_estadual || ""} disabled readOnly />
                </div>
                <div className="input-group" style={{ gridColumn: "1 / -1" }}>
                  <label className="input-label">Razão Social *</label>
                  <input className="input" value={config.razao_social || ""} disabled readOnly />
                  <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
                    Estes três vêm de <Link to="/configuracoes/empresa">Configurações › Minha empresa</Link>.
                  </span>
                </div>
                <div className="input-group">
                  <label className="input-label">Regime tributário *</label>
                  <select className="select" value={config.regime_tributario} disabled={dis}
                          onChange={(e) => set("regime_tributario", e.target.value)}>
                    {REGIMES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="block">
            <div className="block__head"><div className="block__title">Padrões da NF-e (mercadoria)</div></div>
            <div className="block__body">
              <div className="cfg-grid">
                <div className="input-group">
                  <label className="input-label">Série</label>
                  <input className="input" type="number" min={1} value={config.serie_padrao} disabled={dis}
                         onChange={(e) => set("serie_padrao", parseInt(e.target.value, 10) || 1)} />
                </div>
                <div className="input-group">
                  <label className="input-label">Natureza da operação</label>
                  <input className="input" value={config.natureza_operacao_padrao || ""} disabled={dis}
                         onChange={(e) => set("natureza_operacao_padrao", e.target.value)} placeholder="Venda" />
                </div>
                <div className="input-group">
                  <label className="input-label">CFOP estadual</label>
                  <input className="input" value={config.cfop_padrao || ""} disabled={dis}
                         onChange={(e) => set("cfop_padrao", e.target.value)} placeholder="5102" />
                </div>
                <div className="input-group">
                  <label className="input-label">CFOP interestadual</label>
                  <input className="input" value={config.cfop_interestadual || ""} disabled={dis}
                         onChange={(e) => set("cfop_interestadual", e.target.value)} placeholder="6102" />
                </div>
              </div>
            </div>
          </div>

          <div className="block">
            <div className="block__head"><div className="block__title">Padrões da NFS-e (serviço)</div></div>
            <div className="block__body">
              <div className="cfg-grid">
                <div className="input-group">
                  <label className="input-label">Inscrição Municipal</label>
                  <input className="input" value={config.inscricao_municipal || ""} disabled={dis}
                         onChange={(e) => set("inscricao_municipal", e.target.value)} placeholder="obrigatória para emitir NFS-e" />
                </div>
                <div className="input-group">
                  <label className="input-label">Código de serviço padrão (LC 116)</label>
                  <input className="input" value={config.codigo_servico_padrao || ""} disabled={dis}
                         onChange={(e) => set("codigo_servico_padrao", e.target.value)} placeholder="ex.: 14.05" />
                </div>
                <div className="input-group">
                  <label className="input-label">Alíquota ISS padrão (%)</label>
                  <input className="input" type="number" step="0.01" min="0" max="5"
                         value={config.aliquota_iss_padrao ?? ""} disabled={dis}
                         onChange={(e) => set("aliquota_iss_padrao", e.target.value)} placeholder="máx. 5%" />
                </div>
              </div>
              <span className="input-help" style={{ display: "block", marginTop: 8 }}>
                A NFS-e exige inscrição municipal + código de serviço cadastrados aqui antes de emitir.
              </span>
            </div>
          </div>

          {podeConfigurar && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <AsyncButton variant="primary" onClick={handleSalvar} successMessage="Configuração salva">
                <FiSave size={14} aria-hidden="true" /> Salvar alterações
              </AsyncButton>
            </div>
          )}

          <div className="block">
            <div className="block__head"><div className="block__title">Como ativar produção</div></div>
            <div className="block__body" style={{ fontSize: 13, color: "var(--ink-2)" }}>
              <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                <li>Contratar plano em app.enotas.com.br e obter a API key</li>
                <li>Cadastrar o certificado A1 da empresa no painel eNotas</li>
                <li>Preencher CNPJ + IE + razão social acima</li>
                <li>Trocar provedor para "eNotas" + ambiente "Produção"</li>
                <li>Colar o ID da empresa no eNotas e a API key nos campos acima</li>
                <li>Salvar — as próximas notas já vão pelo SEFAZ real</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
